// Shared UI primitives ported from the ImageShield reference design.
// Button, IconButton, Pill, UnderlineTab, Input, Select, Textarea, FileInput,
// EventCover, QRCode.
import React, { useMemo, useState } from "react";
import { Icons } from "./Icons";

/* ---------- Button ---------- */
export function Button({ children, variant = "primary", icon: IconC, className = "", ...rest }) {
  const styles = {
    primary: "bg-brand-600 hover:bg-brand-700 text-white shadow-soft",
    secondary: "bg-white text-slate-700 border border-gray-200 hover:bg-gray-50",
    ghost: "text-slate-600 hover:bg-gray-100",
    danger: "bg-white text-red-600 border border-red-100 hover:bg-red-50",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 font-medium text-sm px-4 py-2.5 rounded-lg transition ${styles[variant]} ${className}`}
      {...rest}
    >
      {IconC ? <IconC className="w-4 h-4" /> : null}
      {children}
    </button>
  );
}

/* ---------- IconButton ---------- */
export function IconButton({ icon: IconC, label, variant = "secondary", className = "", ...rest }) {
  const styles = {
    primary: "bg-brand-600 text-white hover:bg-brand-700",
    secondary: "bg-white border border-gray-200 text-slate-700 hover:bg-gray-50",
  };
  return (
    <button
      aria-label={label}
      title={label}
      className={`w-10 h-10 grid place-items-center rounded-lg transition ${styles[variant]} ${className}`}
      {...rest}
    >
      <IconC className="w-4 h-4" />
    </button>
  );
}

/* ---------- Pill ---------- */
export function Pill({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
        active
          ? "bg-brand-600 text-white shadow-soft"
          : "bg-white text-slate-600 border border-gray-200 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

/* ---------- Underline tab ---------- */
export function UnderlineTab({ active, children, onClick, count }) {
  return (
    <button
      onClick={onClick}
      className={`relative pb-3 pt-2 px-1 text-sm font-semibold transition ${
        active ? "text-brand-600" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      <span className="flex items-center gap-2">
        {children}
        {typeof count === "number" && (
          <span
            className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
              active ? "bg-brand-50 text-brand-600" : "bg-gray-100 text-slate-500"
            }`}
          >
            {count}
          </span>
        )}
      </span>
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-600 rounded-full" />}
    </button>
  );
}

/* ---------- Input ---------- */
export function Input({ label, hint, error, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>}
      <input
        className={`w-full bg-white text-slate-900 placeholder-slate-400 text-sm border border-gray-200 rounded-lg px-3 py-2.5 transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 ${className}`}
        {...rest}
      />
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
      {error && <span className="block text-[11px] text-red-600 mt-1">{error}</span>}
    </label>
  );
}

/* ---------- Select ---------- */
export function Select({ label, options = [], children, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>}
      <select
        className={`w-full bg-white text-slate-900 text-sm border border-gray-200 rounded-lg px-3 py-2.5 transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 ${className}`}
        {...rest}
      >
        {children
          ? children
          : options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
      </select>
    </label>
  );
}

/* ---------- Textarea ---------- */
export function Textarea({ label, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>}
      <textarea
        rows={3}
        className={`w-full bg-white text-slate-900 placeholder-slate-400 text-sm border border-gray-200 rounded-lg px-3 py-2.5 transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 ${className}`}
        {...rest}
      />
    </label>
  );
}

/* ---------- FileInput ---------- */
export function FileInput({ label, accept = "image/*", onChange }) {
  const [name, setName] = useState("");
  return (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>}
      <div className="flex items-center gap-2 text-sm">
        <span className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-slate-700 hover:bg-gray-50">
          <Icons.Upload className="w-4 h-4" />
          Choose file
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              setName(e.target.files?.[0]?.name || "");
              onChange?.(e);
            }}
          />
        </span>
        <span className="text-slate-400 text-xs truncate">{name || "No file chosen"}</span>
      </div>
    </label>
  );
}

/* ---------- Cover placeholder (gradient/striped, no real image) ---------- */
export function EventCover({ kind, className = "", label, src }) {
  const base = "relative w-full h-full overflow-hidden";

  // If a real image URL is provided, render it (with graceful fallback handled by caller).
  if (src) {
    return (
      <div className={`${base} ${className}`}>
        <img src={src} alt={label || "event cover"} className="w-full h-full object-cover" />
      </div>
    );
  }

  const Stripes = ({ color = "#f1f5f9" }) => (
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 10px, rgba(0,0,0,0.04) 10px 11px)`,
      }}
    />
  );

  if (kind === "blank") {
    return (
      <div className={`${base} ${className} bg-gray-50 grid place-items-center text-slate-300`}>
        <Icons.Image className="w-10 h-10" />
      </div>
    );
  }

  // Deterministic tint from the label so each event gets a stable colour.
  const hue = label ? (String(label).length * 47) % 360 : 210;
  const tint = `hsl(${hue} 60% 90%)`;
  return (
    <div className={`${base} ${className}`} style={{ background: tint }}>
      <Stripes color="rgba(255,255,255,0.5)" />
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-mono text-[10px] tracking-wider uppercase bg-white/70 backdrop-blur-sm text-slate-700 px-2 py-1 rounded text-center max-w-[90%] truncate">
          {label || "event cover"}
        </span>
      </div>
    </div>
  );
}

/* ---------- QR Code (decorative SVG; deterministic from seed) ---------- */
export function QRCode({ seed = "imageshield", size = 220, className = "" }) {
  const cells = useMemo(() => {
    let s = 0;
    for (let i = 0; i < String(seed).length; i++) s = (s * 31 + String(seed).charCodeAt(i)) >>> 0;
    const N = 25;
    const rng = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 0xffffffff;
    };
    const grid = Array.from({ length: N }, () => Array(N).fill(false));
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) grid[y][x] = rng() > 0.52;
    const carve = (ox, oy) => {
      for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) grid[oy + y][ox + x] = false;
      for (let y = 0; y < 7; y++) {
        grid[oy + y][ox] = true;
        grid[oy + y][ox + 6] = true;
      }
      for (let x = 0; x < 7; x++) {
        grid[oy][ox + x] = true;
        grid[oy + 6][ox + x] = true;
      }
      for (let y = 2; y < 5; y++) for (let x = 2; x < 5; x++) grid[oy + y][ox + x] = true;
    };
    carve(0, 0);
    carve(N - 7, 0);
    carve(0, N - 7);
    return grid;
  }, [seed]);

  const N = cells.length;
  const cell = size / N;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className}>
      <rect width={size} height={size} fill="#ffffff" />
      {cells.map((row, y) =>
        row.map((on, x) =>
          on ? <rect key={`${x}-${y}`} x={x * cell} y={y * cell} width={cell} height={cell} fill="#0f172a" /> : null
        )
      )}
    </svg>
  );
}
